import { useEffect, useState } from "react";
import { Loader2, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const MB = (n) => `${((n || 0) / 1048576).toFixed(1)} MB`;
const day = (s) => (s || "").slice(0, 10);

/** Pictures nothing references any more. Retiring hides them and keeps the
 *  file for 30 days, so a mistake is always recoverable. */
export default function KbUnusedMediaCard() {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState({});
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/kb/media-unused/list");
      setData(d);
      setPicked({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't work out what's unused");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && !data) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const chosen = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);

  const retire = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/kb/media-unused/retire", { asset_ids: chosen });
      toast.success(`${r.retired} picture${r.retired === 1 ? "" : "s"} retired — deleted for good in ${r.retention_days} days`);
      if (r.skipped_in_use?.length) {
        toast.warning(`${r.skipped_in_use.length} were in use after all and were left alone`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't retire those");
    } finally { setBusy(false); }
  };

  const restore = async (ids) => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/kb/media-unused/restore", { asset_ids: ids });
      toast.success(`${r.restored} put back in the library`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't put those back");
    } finally { setBusy(false); }
  };

  const rows = data?.assets || [];
  const retired = data?.retired || [];

  return (
    <Card className="px-5 py-4" data-testid="kb-unused-media-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-slate-800 dark:text-slate-100">
            <Trash2 className="h-4 w-4 text-slate-400" /> Unused pictures
          </h3>
          <p className="mt-1 max-w-2xl text-xs text-slate-500 dark:text-slate-400">
            Pictures no article, section, saved revision, snippet or glossary entry references any
            more. Retiring one hides it from the library and keeps the file for
            {" "}{data?.retention_days || 30} days, then it is deleted for good. Anything uploaded in
            the last {data?.min_age_days || 7} days is never listed.
          </p>
        </div>
        <Button size="sm" variant={open ? "outline" : "default"} onClick={() => setOpen(!open)}
          data-testid="kb-unused-media-toggle">
          {open ? "Hide" : "Show what's unused"}
        </Button>
      </div>

      {open && (
        <div className="mt-4 border-t border-slate-100 dark:border-slate-800 pt-3">
          {loading && !data ? (
            <div className="flex h-24 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs text-slate-500 dark:text-slate-400" data-testid="kb-unused-media-summary">
                  {data?.count || 0} unused ({MB(data?.bytes)})
                  {data?.too_new ? `, ${data.too_new} too new to judge` : ""}
                  {data?.retired_count ? `, ${data.retired_count} already retired (${MB(data.retired_bytes)})` : ""}
                </span>
                {rows.length > 0 && (
                  <>
                    <button type="button" data-testid="kb-unused-media-all"
                      onClick={() => setPicked(Object.fromEntries(rows.map((a) => [a.asset_id, true])))}
                      className="text-[11px] font-semibold text-primary hover:underline">Tick all</button>
                    <button type="button" data-testid="kb-unused-media-none"
                      onClick={() => setPicked({})}
                      className="text-[11px] font-semibold text-slate-500 hover:underline">Untick all</button>
                    <Button size="sm" variant="destructive" className="ml-auto" onClick={retire}
                      disabled={busy || !chosen.length} data-testid="kb-unused-media-retire">
                      {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        : <Trash2 className="mr-1.5 h-3.5 w-3.5" />}
                      Retire {chosen.length}
                    </Button>
                  </>
                )}
              </div>

              {rows.length === 0 ? (
                <p className="mt-3 text-xs text-slate-500 dark:text-slate-400" data-testid="kb-unused-media-empty">
                  Nothing wasted — every picture we hold is used somewhere.
                </p>
              ) : (
                <div className="mt-3 max-h-[360px] space-y-1 overflow-y-auto">
                  {rows.map((a) => (
                    <label key={a.asset_id} data-testid={`kb-unused-media-row-${a.asset_id}`}
                      className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-100 dark:border-slate-800 px-2.5 py-1.5 hover:border-primary/40">
                      <input type="checkbox" checked={!!picked[a.asset_id]}
                        data-testid={`kb-unused-media-tick-${a.asset_id}`}
                        onChange={(e) => setPicked((s) => ({ ...s, [a.asset_id]: e.target.checked }))}
                        className="h-3.5 w-3.5 accent-primary" />
                      <img src={a.url} alt="" className="h-8 w-12 shrink-0 rounded object-cover" />
                      <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-slate-700 dark:text-slate-200">
                        {a.filename}
                      </span>
                      <span className="shrink-0 text-[11px] text-slate-400 dark:text-slate-500">{MB(a.size)}</span>
                      <span className="hidden shrink-0 text-[11px] text-slate-400 dark:text-slate-500 sm:block">
                        {a.uploaded_by} · {day(a.created_at)}
                      </span>
                    </label>
                  ))}
                </div>
              )}

              {retired.length > 0 && (
                <div className="mt-4 border-t border-dashed border-slate-200 dark:border-slate-800 pt-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                      Retired — waiting to be deleted
                    </span>
                    <Button size="sm" variant="outline" className="ml-auto h-7 text-xs"
                      disabled={busy} data-testid="kb-unused-media-restore-all"
                      onClick={() => restore(retired.map((a) => a.asset_id))}>
                      <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Put them all back
                    </Button>
                  </div>
                  <div className="mt-2 max-h-[200px] space-y-1 overflow-y-auto">
                    {retired.map((a) => (
                      <div key={a.asset_id} data-testid={`kb-unused-media-retired-${a.asset_id}`}
                        className="flex items-center gap-2 rounded-lg border border-slate-100 dark:border-slate-800 px-2.5 py-1.5">
                        <img src={a.url} alt="" className="h-8 w-12 shrink-0 rounded object-cover opacity-60" />
                        <span className="min-w-0 flex-1 truncate text-[11px] text-slate-600 dark:text-slate-300">{a.filename}</span>
                        <span className="shrink-0 text-[11px] text-slate-400 dark:text-slate-500">
                          gone after {day(a.purge_after)}
                        </span>
                        <button type="button" disabled={busy}
                          data-testid={`kb-unused-media-restore-${a.asset_id}`}
                          onClick={() => restore([a.asset_id])}
                          className="shrink-0 text-[11px] font-semibold text-primary hover:underline">
                          Put back
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}
